import { Plus, Check } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export default function FrequentlyBoughtTogether({ product }) {
  const { addItem, setIsOpen } = useCart();
  const { products } = useProducts();

  // Smart complement: prefer opposite category same fragrance, else most popular (by reviews).
  const complement = useMemo(() => {
    const pool = products.filter(p => p.id !== product.id && p.category !== 'bundle' && !p.hidden);
    const byPop = (a, b) => (b.reviews || 0) - (a.reviews || 0);
    if (product.category === 'professional') {
      const pro = pool.filter(p => p.category === 'professional').sort(byPop);
      return pro[0] || [...pool].sort(byPop)[0];
    }
    const opposite = product.category === 'lavapavimenti' ? 'sgrassatori' : 'lavapavimenti';
    const sameFrag = pool.find(p => p.category === opposite && p.fragrance === product.fragrance);
    if (sameFrag) return sameFrag;
    const oppPop = pool.filter(p => p.category === opposite).sort(byPop);
    return oppPop[0] || [...pool].sort(byPop)[0];
  }, [products, product]);

  const [includeMain, setIncludeMain] = useState(true);
  const [includeCompl, setIncludeCompl] = useState(true);

  if (!complement) return null;

  const items = [
    includeMain && product,
    includeCompl && complement,
  ].filter(Boolean);

  const total = items.reduce((s, p) => s + p.price, 0);

  const addCombo = () => {
    if (items.length === 0) return toast.error('Seleziona almeno un prodotto');
    items.forEach(p => addItem(p.id, 1));
    toast.success('Aggiunto al carrello');
    setIsOpen(true);
  };

  return (
    <section data-testid="frequently-bought-together" className="mt-24 bg-essenya-surface2 rounded-2xl p-6 md:p-10">
      <div className="text-eyebrow mb-2">Spesso acquistati insieme</div>
      <h2 className="font-display text-2xl md:text-3xl mb-2">Completa il rituale {product.fragranceLabel}</h2>
      <p className="text-essenya-text mb-8 max-w-xl">Abbina lavapavimenti e sgrassatore della stessa fragranza per una casa coerente in ogni ambiente.</p>

      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
        <ProductTile p={product} checked={includeMain} onToggle={() => setIncludeMain(!includeMain)} testId="fbt-main" />
        <Plus size={20} strokeWidth={1.5} className="mx-auto text-essenya-text flex-shrink-0" />
        <ProductTile p={complement} checked={includeCompl} onToggle={() => setIncludeCompl(!includeCompl)} testId="fbt-complement" />
        <div className="bg-white rounded-xl border border-essenya-border p-5 md:min-w-[240px] flex-shrink-0 flex flex-col">
          <div className="text-eyebrow mb-2">Totale combo</div>
          <p className="font-display text-2xl" data-testid="fbt-total">{total.toFixed(2).replace('.', ',')}€</p>
          <button onClick={addCombo} data-testid="fbt-add-combo" className="btn-primary mt-4 text-sm py-3">Aggiungi al carrello</button>
        </div>
      </div>
    </section>
  );
}

function ProductTile({ p, checked, onToggle, testId }) {
  return (
    <button
      onClick={onToggle}
      data-testid={testId}
      className={`flex-1 text-left bg-white rounded-xl border-2 p-3 flex gap-3 items-center transition-all ${checked ? 'border-essenya-ink' : 'border-essenya-border opacity-60'}`}
    >
      <div className="relative">
        <img src={p.images[0]} alt={p.name} className="w-20 h-24 object-contain p-1 rounded-lg bg-essenya-surface2 flex-shrink-0" />
        <span className={`absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full flex items-center justify-center border-2 ${checked ? 'bg-essenya-ink border-essenya-ink' : 'bg-white border-essenya-border'}`}>
          {checked && <Check size={12} strokeWidth={2.5} className="text-white" />}
        </span>
      </div>
      <div className="min-w-0">
        <p className="text-xs text-essenya-text">{p.categoryLabel}</p>
        <p className="font-medium text-sm leading-tight truncate">{p.name}</p>
        <p className="font-display text-base mt-1">{p.price.toFixed(2).replace('.', ',')}€</p>
      </div>
    </button>
  );
}
